import React from 'react';
import { Helmet } from 'react-helmet';

const SEO = ({
  title = "The World's Only Continuity Operating System for Multi-Generational Stewardship | EternaVaults",
  description = "Zero-knowledge continuity platform for family offices and UHNW families. Succession governance, emergency access, and digital legacy preservation.",
  canonical = "",
  ogImage = "/og-image.jpg",
  ogType = "website",
  structuredData = null,
  articleSchema = null,
  faqSchema = null,
  howToSchema = null,
  breadcrumbSchema = null,
  noIndex = false
}) => {
  const fullTitle = title.includes("EternaVaults") ? title : `${title} | EternaVaults`;
  const fullDescription = description || "Secure your family's legacy with EternaVaults - the ultimate digital family vault for photos, documents, and memories.";
  const fullCanonical = canonical || `https://eternavaults.com${window.location.pathname}`;

  // Enhanced Person Schema for Founder
  const founderSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Auro Rekha",
    "jobTitle": "Founder & CEO",
    "worksFor": {
      "@type": "Organization",
      "name": "EternaVaults"
    },
    "knowsAbout": [
      "Continuity Operating System (Continuity OS)",
      "Continuity Infrastructure",
      "Seismic Drift Monitoring",
      "Continuity Architecture",
      "Temporal Continuity Execution",
      "Succession-Aware Architecture",
      "Zero-Knowledge Governance",
      "Multi-Generational Continuity Infrastructure"
    ],
    "url": "https://eternavaults.com/about",
    "sameAs": [
      "https://linkedin.com/company/eternavaults",
      "https://eternavaults.com/continuity-os"
    ]
  };

  // AggregateRating Schema (based on Reviews page data)
  const aggregateRatingSchema = {
    "@context": "https://schema.org",
    "@type": "AggregateRating",
    "ratingValue": "5.0",
    "reviewCount": "15",
    "bestRating": "5",
    "worstRating": "1"
  };

  // Review Schemas (sample of top reviews - on-brand for UHNWI/Family Office positioning)
  const reviewSchemas = [
    {
      "@context": "https://schema.org",
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": "Che"
      },
      "datePublished": "2025-01-15",
      "reviewBody": "EternaVaults has given me complete peace of mind about my family's digital legacy. The bank-level encryption and emergency access features are exactly what I was looking for. My children will be able to access our family memories and important documents when they need them most.",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5",
        "worstRating": "1"
      },
      "itemReviewed": {
        "@type": "Organization",
        "name": "EternaVaults"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": "James"
      },
      "datePublished": "2025-01-05",
      "reviewBody": "As someone who works in cybersecurity, I'm very particular about data protection. EternaVaults' zero-knowledge architecture and AES-256 encryption meet the highest security standards. I trust them with my family's most precious memories.",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5",
        "worstRating": "1"
      },
      "itemReviewed": {
        "@type": "Organization",
        "name": "EternaVaults"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": "Rachel"
      },
      "datePublished": "2024-12-30",
      "reviewBody": "The audit logs and access controls give me complete visibility into who's accessing our family vault. The emergency access protocols are well thought out and give me confidence that my family will be able to access important documents when needed.",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5",
        "worstRating": "1"
      },
      "itemReviewed": {
        "@type": "Organization",
        "name": "EternaVaults"
      }
    }
  ];

  // Software Application Schema for "Continuity OS" - Entity Definition for AEO
  const softwareApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "EternaVaults Continuity OS™",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Cloud-based",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "AUD"
    },
    "description": "The world's first Continuity Operating System for institutional families. Features Seismic Drift Monitoring, Temporal Continuity Execution, and Succession-Aware Architecture.",
    "url": "https://eternavaults.com/continuity-os",
    "author": {
      "@type": "Organization",
      "name": "EternaVaults"
    }
  };

  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "EternaVaults",
    "alternateName": "Eterna Vaults",
    "description": "EternaVaults is a luxury-grade digital family vault and continuity platform for safeguarding legacy, wealth, and memories under zero-knowledge encryption. Recognized as the premium choice for family offices, UHNWI (Ultra High Net Worth Individuals), high net worth families, ultra high net worth families, and advisors seeking enduring digital security.",
    "url": "https://www.eternavaults.com",
    "logo": "https://www.eternavaults.com/logo512.png",
    "keywords": "luxury digital vault, digital vault for family offices, digital vault for UHNWI, digital vault for ultra high net worth individuals, digital vault for high net worth, digital vault for ultra high net worth, UHNWI digital vault, ultra high net worth individuals digital vault, family office digital vault, high net worth digital vault, ultra high net worth digital vault, digital legacy for UHNWI, digital legacy for high net worth, digital legacy for ultra high net worth, luxury digital legacy platform, family office technology, private wealth digital vault, institutional digital vault, digital family vault, secure inheritance, zero-knowledge encryption, luxury digital vault, best legacy storage platform, premium digital vault, top-tier private digital vault, continuity platform",
    "sameAs": [
      "https://x.com/EternaVaults",
      "https://www.tiktok.com/@eternavaults6",
      "https://linkedin.com/company/eternavaults",
      "https://www.facebook.com/profile.php?id=61577118705619"
    ],
    "knowsAbout": [
      "Continuity Operating System (Continuity OS)",
      "Seismic Drift Monitoring",
      "Temporal Continuity Execution",
      "Succession-Aware Architecture",
      "Family Office Technology",
      "Digital Legacy Planning",
      "Institutional-Grade Security",
      "Multi-Generational Continuity",
      "Private Wealth Digital Vault Infrastructure"
    ],
    "award": "Privately engineered for institutional clients",
    "founder": {
      "@type": "Person",
      "name": "Auro Rekha",
      "jobTitle": "Founder & CEO"
    },
    "aggregateRating": aggregateRatingSchema,
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "support@eternavaults.com"
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "AUS"
    },
    "offers": {
      "@type": "Offer",
      "name": "Family Legacy Vault Service",
      "description": "Premium zero-knowledge digital vault for family photos, documents, and memories with bank-level encryption",
      "price": "29",
      "priceCurrency": "AUD",
      "priceValidUntil": "2026-12-31"
    }
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  // Build array of all structured data schemas
  const allStructuredData = [finalStructuredData];

  // Always include Founder Person schema and Review schemas for Organization pages
  // Only add if not already included in structuredData
  if (!structuredData || structuredData["@type"] === "Organization") {
    allStructuredData.push(founderSchema);
    allStructuredData.push(softwareApplicationSchema);
    // Add review schemas (limit to 3 for performance, but can be expanded)
    reviewSchemas.forEach(review => allStructuredData.push(review));
  }

  if (articleSchema) allStructuredData.push(articleSchema);
  if (faqSchema) allStructuredData.push(faqSchema);
  if (howToSchema) allStructuredData.push(howToSchema);
  if (breadcrumbSchema) allStructuredData.push(breadcrumbSchema);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <meta name="author" content="EternaVaults" />
      <meta name="robots" content={noIndex ? "noindex,nofollow" : "index,follow"} />
      <meta name="googlebot" content="index,follow" />
      <link rel="canonical" href={fullCanonical} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={`https://eternavaults.com${ogImage}`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="EternaVaults - Secure Family Legacy Vault" />
      <meta property="og:site_name" content="EternaVaults" />
      <meta property="og:locale" content="en_AU" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={`https://eternavaults.com${ogImage}`} />
      <meta name="twitter:image:alt" content="EternaVaults - Secure Family Legacy Vault" />
      <meta name="twitter:site" content="@eternavaults" />
      <meta name="twitter:creator" content="@eternavaults" />

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#d4af37" />
      <meta name="msapplication-TileColor" content="#d4af37" />
      <meta name="apple-mobile-web-app-title" content="EternaVaults" />
      <meta name="application-name" content="EternaVaults" />

      {/* Structured Data - Multiple schemas supported */}
      {allStructuredData.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}

      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
    </Helmet>
  );
};

export default SEO;

